import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { MessageCircleReply, Send, FileText, Paperclip, Mail, Globe, X } from "lucide-react";
import { api, API, TOKEN_KEY } from "@/lib/api";
import { fmtDate } from "@/lib/constants";

export function renderTemplate(body, vars) {
  return body.replace(/{{\s*(\w+)\s*}}/g, (_, k) => (vars[k] ?? `{{${k}}}`));
}

function fileUrl(id) {
  return `${API}/files/${id}?auth=${localStorage.getItem(TOKEN_KEY) || ""}`;
}

function Attachments({ items = [], dark }) {
  if (!items.length) return null;
  return (
    <ul className="mt-2 flex flex-wrap gap-2" data-testid="message-attachments">
      {items.map((a) => (
        <li key={a.file_id}>
          {a.available ? (
            a.content_type?.startsWith("image/") ? (
              <a href={fileUrl(a.file_id)} target="_blank" rel="noreferrer" data-testid={`attachment-${a.file_id}`}>
                <img src={fileUrl(a.file_id)} alt={a.filename} className="max-h-48 rounded-lg border-2 border-black bg-white object-contain" />
              </a>
            ) : (
              <a href={fileUrl(a.file_id)} target="_blank" rel="noreferrer" className={`inline-flex items-center gap-1 rounded-lg border-2 px-2 py-1 text-xs font-semibold ${dark ? "border-[#FACC15] text-[#FACC15]" : "border-black bg-white"}`} data-testid={`attachment-${a.file_id}`}>
                <Paperclip className="h-3 w-3" /> {a.filename} <span className="opacity-60">({Math.round(a.size / 1024)} KB)</span>
              </a>
            )
          ) : (
            <span className="inline-flex items-center gap-1 rounded-lg border-2 border-dashed border-zinc-400 px-2 py-1 text-xs text-zinc-500" title="Attachment could not be stored"><Paperclip className="h-3 w-3" /> {a.filename} (unavailable)</span>
          )}
        </li>
      ))}
    </ul>
  );
}

export function MessageList({ messages = [], viewer = "team", testId = "conversation-list" }) {
  const sorted = [...messages].sort((a, b) => (a.created_at < b.created_at ? -1 : 1));
  if (!sorted.length) return <p className="text-sm text-zinc-500" data-testid={`${testId}-empty`}>No messages yet.</p>;
  return (
    <ol className="space-y-2" data-testid={testId}>
      {sorted.map((m) => {
        const own = (viewer === "team" && m.from === "team") || (viewer === "customer" && m.from === "customer");
        const dark = m.from === "team";
        const who = m.from === "team" ? m.author : viewer === "team" ? `${m.author}${m.from_email ? ` <${m.from_email}>` : " (customer)"}` : "You";
        return (
          <li key={m.id} className={`flex ${own ? "justify-end" : "justify-start"}`} data-testid={`message-${m.id}`}>
            <div className={`max-w-[85%] rounded-2xl border-2 px-3 py-2 text-sm ${dark ? "border-black bg-black text-white" : "border-black bg-[#FEF9C3]"}`}>
              {viewer === "team" && m.subject && <p className={`mb-1 text-[11px] font-bold ${dark ? "text-[#FACC15]" : "text-zinc-600"}`} data-testid="message-subject">{m.subject}</p>}
              <p className="whitespace-pre-wrap">{m.text}</p>
              {viewer === "team" && <Attachments items={m.attachments} dark={dark} />}
              <p className={`mt-1 flex flex-wrap items-center gap-1 text-[11px] ${dark ? "text-[#FACC15]" : "text-zinc-600"}`}>
                {m.channel === "email" ? <Mail className="h-3 w-3" /> : <Globe className="h-3 w-3" />}
                {who} · {fmtDate(m.created_at)}
                {m.from === "team" && viewer === "team" && m.email_status && <span title={m.email_error || ""}> · email {m.email_status === "sent" ? "sent" : m.email_status}</span>}
              </p>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

export function TemplatePicker({ vars, onPick, kind = "reply", label = "Saved replies", testId = "template-picker" }) {
  const [open, setOpen] = useState(false);
  const { data: templates = [] } = useQuery({ queryKey: ["templates", kind], queryFn: () => api.get("/templates", { params: { kind } }).then((r) => r.data), staleTime: 120000 });
  if (!templates.length) return null;
  return (
    <div className="relative">
      <button type="button" className="btn-ghost !py-1.5 !text-xs" onClick={() => setOpen(!open)} data-testid={`${testId}-button`}>
        <FileText className="h-3.5 w-3.5" /> {label}
      </button>
      {open && (
        <ul className="absolute bottom-full left-0 z-20 mb-1 w-80 overflow-hidden rounded-xl border-2 border-black bg-white brut-shadow-sm" data-testid={`${testId}-list`}>
          {templates.map((t) => (
            <li key={t.id}>
              <button
                type="button"
                className="block w-full px-3 py-2 text-left text-sm hover:bg-[#FEF9C3]"
                onClick={() => {
                  onPick(renderTemplate(t.body, vars));
                  setOpen(false);
                }}
                data-testid={`template-option-${t.id}`}
              >
                <span className="font-bold">{t.title}</span>
                <span className="block truncate text-xs text-zinc-500">{renderTemplate(t.body, vars).split("\n")[0]}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function ReplyBox({ onSend, busy, placeholder, testId = "reply", label = "Send", templateVars = null, withSubject = false, defaultSubject = "", withFiles = false }) {
  const [text, setText] = useState("");
  const [subject, setSubject] = useState(defaultSubject);
  const [files, setFiles] = useState([]);
  const inputRef = useRef(null);
  const addFiles = (list) => {
    const next = [...files];
    for (const f of Array.from(list)) {
      if (next.length >= 5) { toast.error("Up to 5 files per email"); break; }
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name} is larger than 10 MB`); continue; }
      next.push(f);
    }
    setFiles(next);
  };
  const submit = () => {
    if (text.trim().length < 2) return;
    onSend(text.trim(), () => { setText(""); setFiles([]); }, subject.trim(), files);
  };
  return (
    <div className="space-y-2">
      {templateVars && (
        <div className="flex items-center gap-2">
          <TemplatePicker vars={templateVars} onPick={setText} />
          <span className="text-xs text-zinc-500">Insert a saved reply, then personalise before sending.</span>
        </div>
      )}
      {withSubject && (
        <input className="field-input !py-2 text-sm" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject (ticket number is added automatically)" maxLength={200} data-testid={`${testId}-subject-input`} />
      )}
      <div className="flex gap-2">
        <textarea
          className="field-input min-h-[64px] flex-1 !py-2.5"
          placeholder={placeholder}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit();
          }}
          maxLength={5000}
          data-testid={`${testId}-input`}
        />
        <div className="flex flex-col justify-end gap-1">
          {withFiles && (
            <>
              <input ref={inputRef} type="file" multiple className="hidden" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid={`${testId}-file-input`} />
              <button type="button" className="btn-ghost !px-3 !py-2" onClick={() => inputRef.current?.click()} disabled={busy} title="Attach files (images, PDF, docs · max 10 MB · up to 5)" data-testid={`${testId}-attach-button`}>
                <Paperclip className="h-4 w-4" />
              </button>
            </>
          )}
          <button type="button" className="btn-primary !px-4" onClick={submit} disabled={busy || text.trim().length < 2} data-testid={`${testId}-send-button`} title={label}>
            {busy ? <MessageCircleReply className="h-4 w-4 animate-pulse" /> : <Send className="h-4 w-4" />}
          </button>
        </div>
      </div>
      {files.length > 0 && (
        <ul className="flex flex-wrap gap-2" data-testid={`${testId}-pending-files`}>
          {files.map((f, i) => (
            <li key={`${f.name}-${i}`} className="inline-flex items-center gap-1 rounded-lg border-2 border-black bg-[#FEF9C3] px-2 py-1 text-xs font-semibold" data-testid={`${testId}-pending-file-${i}`}>
              <Paperclip className="h-3 w-3" /> {f.name} <span className="text-zinc-500">({Math.max(1, Math.round(f.size / 1024))} KB)</span>
              <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} className="ml-1 rounded hover:bg-black hover:text-white" aria-label="Remove" data-testid={`${testId}-remove-file-${i}`}><X className="h-3 w-3" /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
